import { useCallback } from 'react';
import { useSession } from '@/shared/hooks/use-session';
import { usePlan } from '@/modules/auth';
import { usePermissions } from '@/shared/access/use-permissions';
import { planAllows } from '@/shared/access/plan-gates';
import type { DashboardRole } from '@/shared/types/session';
import { isPermissionKey, moduleOf, requiresSettingsParent, type AccessKey } from '@/shared/permissions';

/** Why a feature is (un)available. 'plan' and 'permission' both open the popup. */
export type AccessReason = 'ok' | 'permission' | 'plan';

export interface AccessResult {
    allowed: boolean;
    reason:  AccessReason;
}

/** Owners carry full permissions and are never permission-gated. */
function isOwnerRole(role: DashboardRole): boolean {
    return role === 'tenant_admin' || role === 'super_admin';
}

/**
 * Central access check for the signed-in member. Two kinds of member:
 *
 *   - Owners (tenant_admin / super_admin): carry full permissions. Their access
 *     is gated only by their workspace PLAN (see `plan-gates`) — e.g. a free
 *     plan can lock a module behind an upgrade.
 *   - Managed members (builder_user / sales_rep): their access is governed
 *     ENTIRELY by the Role Matrix role assigned to their account — the granted
 *     "module.action" set the API returns at login / hydration. No grant ⇒ the
 *     module disappears from the sidebar and its actions are blocked.
 *
 * Keys outside the matrix catalog (the plan-gated export actions) resolve to
 * the module's `view` grant for managed members.
 */
export function useAccess() {
    const { session } = useSession();
    const permissions = usePermissions();
    const plan = usePlan();

    const isOwner = session ? isOwnerRole(session.role) : true;

    const can = useCallback(
        (feature: AccessKey): AccessResult => {
            // A plan block is workspace-wide and outranks the Role Matrix: no
            // grant can hand out what the plan doesn't include, so this runs
            // for managed members too, not just owners.
            if (!planAllows(feature, plan)) return { allowed: false, reason: 'plan' };
            // Owners hold every permission, so the plan was the only gate.
            if (isOwner) return { allowed: true, reason: 'ok' };
            // Managed member: access == the Role Matrix grants. A missing map
            // means it hasn't hydrated yet — treat as not-granted so gating
            // never leaks access it can't yet verify.
            const key = isPermissionKey(feature) ? feature : `${moduleOf(feature)}.view`;
            let granted = permissions ? !!permissions[key] : false;
            // Settings sections (and the Role Matrix) additionally sit behind
            // the parent "Settings → View" master switch — a section grant
            // alone is not enough when the parent is off.
            if (granted && requiresSettingsParent(key)) {
                granted = !!permissions?.['settings.view'];
            }
            return granted ? { allowed: true, reason: 'ok' } : { allowed: false, reason: 'permission' };
        },
        [isOwner, permissions, plan],
    );

    return { can };
}

/** Convenience boolean check for a single feature. */
export function useCan(feature: AccessKey): boolean {
    return useAccess().can(feature).allowed;
}
